import CSV from "@/UI/CSV";
import Menu from "@/UI/Menu";
import PrintPDF from "@/components/CommonUi/PrintPdf";
import { Button, Popover } from "antd";
import React, { useEffect, useState } from "react";
import { BsDatabaseExclamation, BsThreeDotsVertical } from "react-icons/bs";
import Pagination from "../../UI/Pagination";
import Table from "../../UI/Table";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import CommonSearch from "./CommonSearch";
import Filter from "./Filter";
import Segmented from "@/UI/Segmented";
import { CiCreditCard2, CiViewTable } from "react-icons/ci";
import ResponsiveMobileCard from "./ResponsiveMobileCard";

const TableComponent = ({
  list,
  total,
  loading,
  children,
  filters,
  columns,
  extraFilter,
  title,
  setPageConfig,
  isSearch,
  loadingUiSize = 10,
  nestedRowKey,
  card,
  defaultView = "list",
}) => {
  const fetchData = (page, count) => {
    setPageConfig((prev) => ({ ...prev, page, count }));
  };

  const [view, setView] = useState(defaultView);
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setColumnsToShow(columns);
  }, [columns?.length]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const columnsToShowHandler = (val) => setColumnsToShow(val);
  const handleChange = (value) => setView(value);

  const options = [
    { value: "card", icon: <CiCreditCard2 size={21} />, title: "Card" },
    { value: "list", icon: <CiViewTable size={21} />, title: "List" },
  ];

  const exportMenu = (
    <Menu
      items={[
        {
          key: "1",
          label: (
            <PrintPDF list={list} columns={columns} title={title} notButton />
          ),
        },
        {
          key: "2",
          label: <CSV notButton={true} list={list} columns={columns} title={title} />,
        },
        {
          key: "3",
          label: (
            <ColVisibilityDropdown
              options={columns}
              columns={columns}
              columnsToShowHandler={columnsToShowHandler}
            />
          ),
        },
      ]}
    />
  );

  return (
    <>
      {/* ===== MOBILE TOOLBAR ===== */}
      {isMobile && (
        <div className="w-full mb-2 px-3 mt-2">
          {isSearch && (
            <div className="w-full mb-2 mt-2">
              <CommonSearch setPageConfig={setPageConfig} />
            </div>
          )}
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <Filter
                setPageConfig={setPageConfig}
                filters={filters}
                extraFilter={extraFilter}
              />
            </div>
            <Popover
              content={exportMenu}
              placement="bottomRight"
              arrow={false}
              trigger="click"
            >
              <Button
                type="default"
                icon={<BsThreeDotsVertical size={18} />}
                className="px-3 ml-2"
              />
            </Popover>
          </div>
        </div>
      )}

      {/* ===== DESKTOP TOOLBAR ===== */}
      <div className="mt-2">
        <div className="w-full px-3 flex flex-wrap gap-2 items-center flex-col-reverse sm:flex-row justify-between mb-3">
          <div className="flex flex-wrap lg:flex-nowrap gap-2 w-full md:w-auto">
            {!isMobile && (
              <>
                {isSearch && (
                  <div className="w-full sm:w-[250px]">
                    <CommonSearch setPageConfig={setPageConfig} />
                  </div>
                )}
                <div className="hideScrollBar overflow-x-auto w-full flex items-center gap-2">
                  <Filter
                    setPageConfig={setPageConfig}
                    filters={filters}
                    extraFilter={extraFilter}
                  />
                  {!card ? (
                    <ColVisibilityDropdown
                      options={columns}
                      columns={columns}
                      columnsToShowHandler={columnsToShowHandler}
                    />
                  ) : (
                    <Segmented
                      options={options}
                      value={view}
                      onChange={handleChange}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {/* ===== RIGHT SIDE ===== */}
          {!isMobile && (
            <div className="flex items-center gap-2">
              <Button className="gap-1 text-xs">
                <PrintPDF list={list} columns={columns} title={title} />
              </Button>
              <Button className="gap-1 text-xs">
                <CSV notButton={true} list={list} columns={columns} title={title} />
              </Button>
            </div>
          )}
        </div>

        {/* ===== DISPLAY AREA ===== */}
        {isMobile ? (
          // ✅ MOBILE CARD VIEW
          <>
            <div className="grid grid-cols-1 gap-4 px-3 pb-3">
              {list?.map((item, idx) => (
                <ResponsiveMobileCard key={idx} item={item} columns={columnsToShow} />
              ))}
            </div>
            {!list?.length && !loading && (
              <div className="flex flex-col justify-center items-center h-full py-10">
                <BsDatabaseExclamation className="text-slate-200" size={70} />
                <span className="py-2 text-lg text-slate-500">No Records Found</span>
              </div>
            )}
          </>
        ) : (
          // ✅ DESKTOP TABLE VIEW
          <Table
            loading={loading}
            columns={columnsToShow}
            nestedRowKey={nestedRowKey}
            data={
              !!list?.length && list.map((item) => ({ ...item, key: item?.id }))
            }
            scroll={list?.length > 15 ? { y: window.innerHeight - 350 } : {}}
            loadingUiSize={loadingUiSize}
          />
        )}
      </div>

      {/* ===== PAGINATION ===== */}
      <div className="flex justify-center mt-3 pt-2 pb-3">
        {total >= 11 && <Pagination onChange={fetchData} total={total} />}
      </div>

      {children && children}
    </>
  );
};

export default TableComponent;
